export const getIntegrationName = (...sources) => {
  for (const source of sources) {
    const value =
      source?.INTEGRATION_NAME ??
      source?.integration_name ??
      source?.IntegrationName ??
      source?.integrationName;

    if (value) return String(value).trim().toUpperCase();
  }

  return "";
};

export const isD365Integration = (...sources) =>
  getIntegrationName(...sources) === "D365BC";

export const isAcombagoIntegration = (...sources) =>
  getIntegrationName(...sources) === "ACOMBAGO";

export const isAcombaIntegration = (...sources) =>
  ["ACOMBAGO", "ACOMBASDK"].includes(getIntegrationName(...sources));

export const isAcombaSdkIntegration = (...sources) =>
  getIntegrationName(...sources) === "ACOMBASDK";

export const normalizeClassDimensionList = (rows) =>
  Array.isArray(rows)
    ? rows
        .filter((row) => row?.dimension_level || row?.class_name)
        .map((row, index) => {
          if (row?.dimension_level) {
            return {
              ...row,
              id: String(row.id ?? "").trim(),
              code: String(row.code ?? row.id ?? "").trim(),
              description: String(row.displayName ?? row.description ?? row.code ?? "").trim(),
              displayName: String(row.displayName ?? row.description ?? row.code ?? "").trim(),
              dimension_level: String(row.dimension_level ?? "").trim(),
              dimension_code: String(row.dimension_code ?? "").trim(),
            };
          }

          return {
            id: index.toString(),
            description: String(row.class_name ?? "").trim(),
          };
        })
        .filter((row) =>
          row.dimension_level ? row.dimension_level && row.id : row.description || row.displayName || row.code
        )
    : [];

export const getD365DimensionLevels = (dimensionRows) => {
  const levels = [];

  (dimensionRows || []).forEach((row) => {
    const level = String(row?.dimension_level || "").trim();
    if (level && !levels.includes(level)) levels.push(level);
  });

  return levels;
};

export const parseD365DimensionValue = (value) => {
  if (!value) return {};

  try {
    let parsed = value;

    for (let index = 0; index < 3 && typeof parsed === "string"; index += 1) {
      parsed = JSON.parse(parsed);
    }

    const rawDimensions =
      parsed?.d365_dimensions ??
      parsed?.D365_DIMENSIONS ??
      parsed?.dimensions ??
      parsed;

    const dimensions =
      typeof rawDimensions === "string"
        ? JSON.parse(rawDimensions)
        : Array.isArray(rawDimensions)
        ? rawDimensions
        : [];

    return dimensions.reduce((acc, item) => {
      const level = String(item?.dimension_level ?? item?.dimensionLevel ?? "").trim();
      if (level) {
        acc[level] = {
          ...item,
          dimension_level: level,
          dimension_code: String(item?.dimension_code ?? "").trim(),
          id: String(item?.id ?? "").trim(),
          code: String(item?.code ?? "").trim(),
          dimensionId: String(item?.dimensionId ?? "").trim(),
          displayName: String(item?.displayName ?? item?.description ?? "").trim(),
        };
      }
      return acc;
    }, {});
  } catch (err) {
    return {};
  }
};

export const buildD365DimensionValue = (selectedByLevel) =>
  JSON.stringify({
    d365_dimensions: Object.keys(selectedByLevel || {})
      .map((level) => selectedByLevel[level])
      .filter(Boolean)
      .map((item) => ({
        dimension_level: item.dimension_level,
        dimension_code: item.dimension_code ?? "",
        id: item.id,
        code: item.code,
        dimensionId: item.dimensionId,
        displayName: item.displayName,
      })),
  });

export const formatD365DimensionValue = (value) => {
  const selectedByLevel = parseD365DimensionValue(value);
  const parts = Object.keys(selectedByLevel).map((level) => {
    const item = selectedByLevel[level];
    return `${level}: ${item.code || item.displayName || item.id || ""}`;
  });

  return parts.length > 0 ? parts.join(", ") : value;
};

export const formatD365DimensionTooltip = (value) => {
  const selectedByLevel = parseD365DimensionValue(value);
  const parts = Object.keys(selectedByLevel).map((level) => {
    const item = selectedByLevel[level];
    return `${level}: ${item.code || item.displayName || item.id || ""}`;
  });

  return parts.length > 0 ? parts.join("\n") : value;
};

export const hasD365DimensionMapping = (value) =>
  Object.keys(parseD365DimensionValue(value)).length > 0;

export const getD365DimensionMappingValue = (item) =>
  item?.d365_dimensions ??
  item?.d365_dimension_mapping ??
  item?.dimension_mapping ??
  item?.dimensions ??
  "";
